import type { FieldErrors } from 'react-hook-form';

/**
 * Rezumatul de erori din formularul de preluare a deseurilor: numele TEHNIC al
 * campului -> eticheta pe care utilizatorul chiar o vede in formular.
 *
 * De ce exista: rezumatul afisa cheia bruta din schema (`becuriSiNeoaneKg`,
 * `dataOrientativaColectare`), adica un nume pe care nu-l poate cauta nimeni pe
 * ecran. Blocul era marcat „Debug: Show validation errors" in cod si randat
 * utilizatorului, in productie.
 *
 * ⚠️ Etichetele sunt COPIATE din `FormLabel`-urile formularului, cu diacritice,
 * si trebuie sa ramana identice cu ele. Un rezumat care numeste campul altfel
 * decat eticheta de langa camp e mai rau decat numele tehnic: trimite la un
 * camp care nu exista sub numele ala.
 *
 * ⚠️ Doua chei NU au `FormLabel` propriu si sunt formulate aici:
 *   · `photos`  — eticheta vine din `PhotoUploadField` (`label="Fotografii Deșeuri:"`)
 *   · `consent` — eticheta din formular e o propozitie intreaga cu link, nefolosibila
 *                 intr-o lista; se rezuma la ce bifezi.
 */
export const ETICHETE_CAMPURI: Record<string, string> = {
  contactPersonName: 'Denumire Persoană Contact',
  contactPersonEmail: 'Email Persoană Contact',
  contactPersonPhone: 'Telefon Persoană Contact',
  dataOrientativaColectare: 'Data Orientativă Colectare',
  pickupAddressSameWithSchoolAddress: 'Adresa de Colectare',
  deeeKg: 'Kilograme DEEE',
  dbaKg: 'Kilograme DBA',
  becuriSiNeoaneKg: 'Kilograme Becuri și Neoane',
  photos: 'Fotografii Deșeuri',
  consent: 'Acordul privind prelucrarea datelor',

  /* Subcampurile adresei de colectare (`SchoolAddressFieldset`, prefix
     `pickupAddress`). Fara ele, o eroare pe judet ajungea in rezumat ca o
     singura linie „pickupAddress", fara mesaj — vezi `aplatizeaza`. */
  'pickupAddress.judet': 'Adresa de colectare — Județ',
  'pickupAddress.oras': 'Adresa de colectare — Oraș',
  'pickupAddress.comuna': 'Adresa de colectare — Comună',
  'pickupAddress.sat': 'Adresa de colectare — Sat',
  'pickupAddress.strada': 'Adresa de colectare — Stradă',
  'pickupAddress.numar': 'Adresa de colectare — Număr',
  'pickupAddress.codPostal': 'Adresa de colectare — Cod poștal',
  'pickupAddress.bloc': 'Adresa de colectare — Bloc',
  'pickupAddress.scara': 'Adresa de colectare — Scară',
  'pickupAddress.etaj': 'Adresa de colectare — Etaj',
  'pickupAddress.apartament': 'Adresa de colectare — Apartament',

  /* Eroarea de GRUP a cantitatilor (niciun prag minim atins), pusa din pagina cu `setError` pe
     `root.cantitati` — vezi `praguri-comanda.ts`. Eticheta e titlul sectiunii, nu un camp. */
  'root.cantitati': 'Cantități deșeuri',
};

/** O eroare gata de afisat: eticheta campului si mesajul lui. */
export interface EroareAfisabila {
  /** Cheia tehnica, doar ca `key` de randare — nu se afiseaza. */
  cheie: string;
  eticheta: string;
  mesaj: string;
}

/**
 * Un nod din arborele de erori al lui react-hook-form e fie o eroare (are
 * `message`), fie un obiect cu erori pe subcampuri. Se recunoaste dupa `message`,
 * nu dupa forma, fiindca un `FieldError` are si `type`, si `ref`.
 */
function esteEroare(nod: unknown): nod is { message?: unknown } {
  return typeof nod === 'object' && nod !== null && 'message' in nod;
}

/**
 * Aplatizeaza arborele de erori la o lista de perechi `cale -> mesaj`.
 *
 * 🔴 De ce nu ajunge `Object.entries(errors)`: erorile pe adresa vin grupate sub
 * `pickupAddress`, ca obiect FARA `message`. Iterat la un nivel, dadea o linie
 * „pickupAddress" cu mesaj gol, indiferent cate subcampuri erau gresite si care.
 */
export function aplatizeazaErori(erori: FieldErrors, prefix = ''): Array<{ cheie: string; mesaj: string }> {
  const iesire: Array<{ cheie: string; mesaj: string }> = [];
  for (const [nume, nod] of Object.entries(erori ?? {})) {
    const cale = prefix ? `${prefix}.${nume}` : nume;
    if (esteEroare(nod) && typeof nod.message === 'string' && nod.message) {
      iesire.push({ cheie: cale, mesaj: nod.message });
    } else if (typeof nod === 'object' && nod !== null) {
      iesire.push(...aplatizeazaErori(nod as FieldErrors, cale));
    }
  }
  return iesire;
}

/**
 * Lista finala pentru rezumat: eticheta romaneasca a campului + mesajul lui.
 *
 * ⚠️ Rezerva pentru o cheie nemapata e CHEIA INSASI, nu un text generic. Un camp
 * adaugat in schema fara linie aici trebuie sa se vada — „Eroare necunoscuta" ar
 * ascunde exact omisiunea. Acelasi motiv pentru care `ETICHETE_CAMPURI` are test:
 * un camp nou fara eticheta pica suita, nu ajunge tacut in fata utilizatorului.
 */
export function erorileFormularului(erori: FieldErrors): EroareAfisabila[] {
  return aplatizeazaErori(erori).map(({ cheie, mesaj }) => ({
    cheie,
    eticheta: ETICHETE_CAMPURI[cheie] ?? cheie,
    mesaj,
  }));
}
